import { useForm, Controller } from "react-hook-form";
import { IClienteForm } from "../../../types/cliente";
import { TipoClienteEnum } from "../../../enums/tipoCliente";
import { useCallback, useEffect, useState } from "react";
import { Ibge } from "../../../services/Ibge";
import { maskCep, maskCnpj, maskCpf, maskPhone } from "../../../utils/masks";
import { toast } from "react-toastify";
import { ISelectOptions } from "../../../types/inputs";
import { useContextSite } from "../../../context/Context";
import { ViaCep } from "../../../services/ViaCep";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";

const cpfCnpjRegex =
  /(^\d{3}\.\d{3}\.\d{3}-\d{2}$)|(^\d{2}\.\d{3}\.\d{3}\/\d{4}-\d{2}$)/;

const TipoOptions = Object.values(TipoClienteEnum).map((i) => ({
  value: i,
  label: i,
}));

const schemaEndereco = z.object({
  bairro: z.string().min(1, "Campo obrigatorio"),
  cep: z.string().min(9, "Campo obrigatorio"),
  cidade: z.string().min(2, "Campo obrigatorio"),
  complemento: z.string().optional().or(z.literal("")),
  logradouro: z.string().min(1, "Campo obrigatorio"),
  numero: z.string().min(1, "Campo obrigatorio"),
  uf: z.string().min(2, "Campo obrigatorio"),
});
const schema = z.object({
  cpfCnpj: z
    .string()
    .min(1, "Campo obrigatório")
    .regex(cpfCnpjRegex, "CPF/CNPJ inválido"),
  email: z.string().email("Email invalido").optional().or(z.literal("")),
  nome: z.string().min(5, "Campo obrigatorio"),
  telefone: z.string().min(9, "Campo obrigatorio"),
  tipo: z.string().min(2, "Campo obrigatorio"),
  endereco: schemaEndereco,
});

export const useFormClientRegister = () => {
  const [ufOptions, setUfOptions] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );
  const { setIsLoad } = useContextSite();
  const [cidadeTemp, setCidadeTemp] = useState("");
  const [cidades, setCidades] = useState<ISelectOptions[]>(
    [] as ISelectOptions[]
  );

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    register,
    formState: { errors },
  } = useForm<IClienteForm>({
    defaultValues: {
      cpfCnpj: "",
      email: "",
      nome: "",
      telefone: "",
      tipo: "" as TipoClienteEnum,
      endereco: {
        bairro: "",
        cep: "",
        cidade: "",
        complemento: "",
        logradouro: "",
        numero: "",
        uf: "",
      },
    },
    resolver: zodResolver(schema),
    mode: "all",
  });

  const getOptions = useCallback(() => {
    Ibge.UFs()
      .then(({ data }) => {
        const options = data.map((i) => ({
          value: i.sigla,
          label: i.sigla,
          element: i,
        }));

        setUfOptions(options);
      })
      .catch(
        ({
          response: {
            data: { mensagem },
          },
        }) => toast.error(mensagem)
      );
  }, []);

  useEffect(() => {
    if (watch("endereco.uf")) {
      setValue("endereco.cidade", "");
      Ibge.CidadesPorEstado({ sigla: watch("endereco.uf") })
        .then(({ data }) => {
          const options = data.map((item) => ({
            value: item.nome,
            label: item.nome,
            element: item,
          }));
          setCidades(options);
          setValue("endereco.cidade", cidadeTemp);
        })
        .then(() => {
          setCidadeTemp("");
        })
        .catch((erro) => toast.error("Erro ao requisitar a lista de cidades"));
    }
  }, [watch("endereco.uf")]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(watch("endereco.cep")));
  }, [watch("endereco.cep")]);

  useEffect(() => {
    setValue("endereco.cep", maskCep(watch("endereco.cep")));
  }, [watch("endereco.cep")]);

  useEffect(() => {
    setValue("telefone", maskPhone(watch("telefone")));
  }, [watch("telefone")]);

  useEffect(() => {
    const input =
      watch("cpfCnpj")?.length <= 14
        ? maskCpf(watch("cpfCnpj"))
        : maskCnpj(watch("cpfCnpj"));
    setValue("cpfCnpj", input);
  }, [watch("cpfCnpj")]);

  function handleCep() {
    if (watch("endereco.cep").length === 9) {
      setIsLoad(true);
      setTimeout(() => {
        ViaCep.get(watch("endereco.cep"))
          .then(({ data }) => {
            setValue("endereco.logradouro", data.logradouro);
            setValue("endereco.bairro", data.bairro);
            // setValue("endereco.cidade", data.localidade);
            setValue("endereco.uf", data.uf);
            setCidadeTemp(data.localidade);
          })
          .catch((erro) => toast.error("Cep não encontrado"))
          .finally(() => setIsLoad(false));
      }, 1000);
    }
  }

  useEffect(() => {
    getOptions();
  }, []);

  return {
    Controller,
    control,
    handleSubmit,
    register,
    errors,
    TipoOptions,
    watch,
    ufOptions,
    cidades,
    handleCep,
  };
};
